import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Checkbox,
  Chip,
  ErrorState,
  Field,
  FormActions,
  FormSection,
  Input,
  Nota,
  PageHeader,
  Select,
  Skeleton,
  Tabs,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';
import HistoricoEdicoes from '../../../components/ti/HistoricoEdicoes';
import ResumoRegistro from '../../../components/ti/ResumoRegistro';

import { TIPLANOS } from '../routes';
import {
  ErroPlanoCotacao,
  ItemPlano,
  PlanoCotacaoHistorico,
  PlanoCotacaoResumo,
  buscarPlanoCotacao,
  historicoPlanoCotacao,
  listarPlanosCotacao,
  salvarPlanoCotacao,
} from '../../../api/plano-cotacao/planoCotacaoApi';
import { formatarDataCurta } from '../../../utils/data';
import { planoAtivoSemConteudo, rotuloTipoNoSga } from '../../../utils/planoCotacaoRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * A ficha de um plano de cotação — **cria e edita com o mesmo formulário**
 * (at-69, frente D/D7).
 *
 * ## Por que sub-rota e não modal
 *
 * São três seções com N itens de dois campos cada, reordenáveis, mais o nome, a
 * cópia de outro plano e a situação — não cabe nos 672px do `Modal` do kit, e já
 * se comportava como tela. URL própria dá "Voltar", **recarregar sem perder tudo**
 * e link entre TIs. Mesma decisão da ficha de usuário (at-68), com Victor.
 *
 * 🔴 **E resolve um defeito por construção:** o modal fechava por Esc e clique
 * fora **com o `PUT` em voo** — nenhum dos listeners consultava `salvando`.
 *
 * ## O 404 é o modo de CRIAÇÃO
 *
 * `GET /plano-cotacao/:codigo` responde 404 para tipo de veículo que ainda não tem
 * plano, e é assim que se cadastra um (at-43). Qualquer **outro** erro é falha de
 * verdade e vira `ErrorState` — o modal antigo já fazia essa distinção, e ela é
 * preservada.
 *
 * ⚠️ **`descricaoTipo` e `planosDisponiveis` vêm por `location.state`**, com
 * fallback: abrir por link direto (state vazio) faz a ficha chamar
 * `listarPlanosCotacao()` ela mesma. São 29 tipos — barato, e é o que faz o
 * endereço funcionar de verdade.
 */

type Estado = 'carregando' | 'ok' | 'erro';
type Aba = 'conteudo' | 'historico';
type Secao = 'coberturas' | 'assistencias' | 'beneficios';

/** Item com id de CLIENTE — a `key` de uma lista reordenável não pode ser índice. */
interface ItemEditavel extends ItemPlano {
  chave: string;
}

const ID_BASE = 'plano-ficha';

const ABAS = [
  { id: 'conteudo', rotulo: 'Conteúdo' },
  { id: 'historico', rotulo: 'Histórico' },
];

const SECOES: Secao[] = ['coberturas', 'assistencias', 'beneficios'];

const ROTULOS: Record<Secao, string> = {
  coberturas: 'Coberturas',
  assistencias: 'Assistências',
  beneficios: 'Benefícios',
};

const vazio = (): Record<Secao, ItemEditavel[]> => ({
  coberturas: [],
  assistencias: [],
  beneficios: [],
});

const PlanoFichaPage: React.FC = () => {
  const voltar = useVoltar(TIPLANOS());
  const { codigo = '' } = useParams<{ codigo: string }>();
  const location = useLocation();

  const doState = (location.state ?? {}) as {
    descricaoTipo?: string | null;
    planosDisponiveis?: PlanoCotacaoResumo[];
  };

  const [descricaoTipo, setDescricaoTipo] = useState<string | null>(doState.descricaoTipo ?? null);
  const [planosDisponiveis, setPlanosDisponiveis] = useState<PlanoCotacaoResumo[]>(
    doState.planosDisponiveis ?? [],
  );

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState<Aba>('conteudo');

  /** false = o código ainda não tem plano (404): a ficha está CRIANDO um. */
  const [existente, setExistente] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [secoes, setSecoes] = useState<Record<Secao, ItemEditavel[]>>(vazio());

  const [salvando, setSalvando] = useState(false);
  const [erroNome, setErroNome] = useState('');
  const [erroItens, setErroItens] = useState<Record<string, string>>({});
  const [erroAtivo, setErroAtivo] = useState('');

  const [origem, setOrigem] = useState('');
  const [copiando, setCopiando] = useState(false);
  const [confirmarCopiaDe, setConfirmarCopiaDe] = useState<string | null>(null);

  const [historico, setHistorico] = useState<PlanoCotacaoHistorico[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState('');

  /* Contador de ids do cliente. `useRef` porque ele não pode reiniciar a cada
     render — dois itens com a mesma `key` fariam o React remontar inputs. */
  const proximaChave = useRef(0);
  const comChave = useCallback(
    (itens: ItemPlano[]): ItemEditavel[] =>
      itens.map((item) => ({ ...item, chave: `i${(proximaChave.current += 1)}` })),
    [],
  );

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const plano = await buscarPlanoCotacao(codigo);
      setExistente(true);
      setNome(plano.nome);
      setAtivo(plano.ativo);
      setAtualizadoEm(plano.atualizadoEm);
      if (plano.descricaoTipoVeiculo) setDescricaoTipo(plano.descricaoTipoVeiculo);
      setSecoes({
        coberturas: comChave(plano.coberturas ?? []),
        assistencias: comChave(plano.assistencias ?? []),
        beneficios: comChave(plano.beneficios ?? []),
      });
      setEstado('ok');
    } catch (falha) {
      // 404 é o caminho normal de CRIAÇÃO (tipo ainda sem plano) — não é erro.
      if ((falha as ErroPlanoCotacao)?.status === 404) {
        setExistente(false);
        setNome(doState.descricaoTipo ?? '');
        setSecoes(vazio());
        setEstado('ok');
        return;
      }

      setErro(extrairMensagemErro(falha, 'Não foi possível carregar o plano.'));
      setEstado('erro');
    }
    // `doState.descricaoTipo` é lido uma vez, na criação — não é dependência viva.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo, comChave]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /* Fallback do `location.state`: quem abre por link direto (ou dá F5) precisa da
     lista para a ação "copiar conteúdo de". São 29 tipos — barato. */
  useEffect(() => {
    if (planosDisponiveis.length > 0) return;

    let vivo = true;
    listarPlanosCotacao()
      .then((lista) => {
        if (vivo) setPlanosDisponiveis(lista.planos);
      })
      .catch(() => {
        // Acessório: sem a lista, só a ação de copiar deixa de ser oferecida.
      });

    return () => {
      vivo = false;
    };
  }, [planosDisponiveis.length]);

  const carregarHistorico = useCallback(async () => {
    if (!existente) return;

    setCarregandoHistorico(true);
    setErroHistorico('');
    try {
      setHistorico(await historicoPlanoCotacao(codigo));
    } catch (falha) {
      setHistorico([]);
      setErroHistorico(extrairMensagemErro(falha, 'Falha ao ler o histórico.'));
    } finally {
      setCarregandoHistorico(false);
    }
  }, [codigo, existente]);

  // Só quando a aba abre: é dado de uma aba, e a maioria das visitas não a usa.
  useEffect(() => {
    if (aba === 'historico') void carregarHistorico();
  }, [aba, carregarHistorico]);

  const alterarItem = (secao: Secao, chave: string, campo: keyof ItemPlano, valor: string) => {
    setSecoes((atual) => ({
      ...atual,
      [secao]: atual[secao].map((item) =>
        item.chave === chave ? { ...item, [campo]: valor } : item,
      ),
    }));
    setErroItens((atual) => ({ ...atual, [chave]: '' }));
  };

  const adicionarItem = (secao: Secao) => {
    setSecoes((atual) => ({
      ...atual,
      [secao]: [...atual[secao], { descricao: '', chave: `i${(proximaChave.current += 1)}` }],
    }));
    setErroAtivo('');
  };

  const removerItem = (secao: Secao, chave: string) => {
    setSecoes((atual) => ({
      ...atual,
      [secao]: atual[secao].filter((item) => item.chave !== chave),
    }));
  };

  const moverItem = (secao: Secao, indice: number, direcao: -1 | 1) => {
    const destino = indice + direcao;
    setSecoes((atual) => {
      const lista = atual[secao];
      if (destino < 0 || destino >= lista.length) return atual;
      const copia = [...lista];
      [copia[indice], copia[destino]] = [copia[destino], copia[indice]];
      return { ...atual, [secao]: copia };
    });
  };

  const totalItens =
    secoes.coberturas.length + secoes.assistencias.length + secoes.beneficios.length;

  /** Só planos COM conteúdo servem de origem — e nunca o próprio. */
  const origensPossiveis = planosDisponiveis.filter(
    (p) =>
      p.codigo !== codigo && p.totalCoberturas + p.totalAssistencias + p.totalBeneficios > 0,
  );

  const copiarConteudoDe = async (deOndeCodigo: string) => {
    setConfirmarCopiaDe(null);
    setCopiando(true);
    try {
      const plano = await buscarPlanoCotacao(deOndeCodigo);
      // Copia as TRÊS seções. Não toca no nome nem em `ativo` (D7).
      setSecoes({
        coberturas: comChave(plano.coberturas ?? []),
        assistencias: comChave(plano.assistencias ?? []),
        beneficios: comChave(plano.beneficios ?? []),
      });
      setErroItens({});
      setErroAtivo('');
      toast.success(`Conteúdo copiado de "${plano.nome}". Ajuste o que for diferente e salve.`);
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao copiar o conteúdo'));
    } finally {
      setCopiando(false);
    }
  };

  const pedirCopia = () => {
    if (!origem) return;
    // Sobrescrever trabalho já digitado exige confirmação (at-24).
    if (totalItens > 0) {
      setConfirmarCopiaDe(origem);
      return;
    }
    void copiarConteudoDe(origem);
  };

  /**
   * Leva o primeiro erro para a área visível.
   *
   * ⚠️ Chamada DEFENSIVA: `scrollIntoView` não existe no jsdom nem em toda WebView
   * antiga, e uma exceção aqui abortaria a validação — o erro nem chegaria a
   * aparecer. Mesmo padrão de `FormularioAssociado`/`FormularioVeiculo`.
   */
  const rolarAte = (campo: string) => {
    const alvo = document.querySelector<HTMLElement>(`[data-field="${campo}"]`);
    if (typeof alvo?.scrollIntoView === 'function') {
      alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const salvar = async () => {
    setErroNome('');
    setErroItens({});
    setErroAtivo('');

    if (!nome.trim()) {
      setErroNome('Informe o nome do plano.');
      rolarAte('nome');
      return;
    }

    /* 🔴 O erro vai JUNTO do item, e a tela rola até ele — antes era um toast
       genérico ("Há item sem descrição em Coberturas") que não dizia qual. */
    const faltando: Record<string, string> = {};
    for (const secao of SECOES) {
      for (const item of secoes[secao]) {
        if (!item.descricao.trim()) faltando[item.chave] = 'Descreva o item ou remova-o.';
      }
    }

    if (Object.keys(faltando).length > 0) {
      setErroItens(faltando);
      rolarAte(`item-${Object.keys(faltando)[0]}`);
      return;
    }

    // Pré-checagem do 400 do backend: plano ativo precisa ter conteúdo (at-43).
    if (planoAtivoSemConteudo(ativo, secoes)) {
      setErroAtivo(
        'Este plano está ativo e não tem nenhum item. Acrescente conteúdo às seções acima ou desmarque "Plano ativo".',
      );
      return;
    }

    const limpar = (itens: ItemEditavel[]): ItemPlano[] =>
      itens.map((item) => {
        const descricao = item.descricao.trim();
        const detalhe = item.detalhe?.trim();
        // `detalhe` vazio é OMITIDO, não enviado como "" — a coluna direita do PDF
        // fica em branco de qualquer forma, e o dado guardado fica limpo.
        return detalhe ? { descricao, detalhe } : { descricao };
      });

    setSalvando(true);
    try {
      const resposta = await salvarPlanoCotacao(codigo, {
        nome: nome.trim(),
        coberturas: limpar(secoes.coberturas),
        assistencias: limpar(secoes.assistencias),
        beneficios: limpar(secoes.beneficios),
        ativo,
      });

      toast.success(
        resposta.criado
          ? 'Plano criado. O tipo de veículo já pode ser cotado.'
          : 'Plano atualizado. Vale para as próximas cotações.',
      );

      setExistente(true);
      setAtualizadoEm(resposta.plano?.atualizadoEm ?? new Date().toISOString());
      if (aba === 'historico') void carregarHistorico();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao salvar o plano'));
    } finally {
      setSalvando(false);
    }
  };

  const resumoDoPlano = planosDisponiveis.find((p) => p.codigo === codigo);
  const alertaTipo = resumoDoPlano ? rotuloTipoNoSga(resumoDoPlano) : null;

  const painelConteudo = () => (
    <div className="tw-flex tw-flex-col tw-gap-8">
      <FormSection titulo="Identificação" icone="identificacao">
        <Field
          rotulo="Nome do plano"
          obrigatorio
          dica="Aparece nesta tela de administração; o PDF não mostra este nome."
          erro={erroNome}
          campo="nome"
        >
          <Input
            value={nome}
            maxLength={120}
            placeholder="Ex.: Exclusive 04/25"
            onChange={(evento) => {
              setNome(evento.target.value);
              setErroNome('');
            }}
          />
        </Field>
      </FormSection>

      {origensPossiveis.length > 0 && (
        <FormSection titulo="Copiar conteúdo de outro plano" icone="documento">
          <Nota>
            Preenche as três seções com o conteúdo do plano escolhido. O nome e a situação{' '}
            <strong>não</strong> são copiados — o conteúdo dos planos costuma ser quase igual,
            mudando só detalhes como proteção a terceiros e dias de carro reserva.
          </Nota>

          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
            <Field rotulo="Plano de origem" className="tw-min-w-0 tw-flex-1 tw-basis-64" campo="origem">
              {/* 🔴 O `<select>` antigo ERA o gatilho (escolher já copiava), com
                  `value=""` fixo. Escolher e agir viraram dois gestos: um select
                  que age é um menu disfarçado de campo. */}
              <Select
                opcoes={origensPossiveis.map((p) => ({
                  valor: p.codigo,
                  rotulo: `${p.nome} (${p.totalCoberturas} cob. · ${p.totalAssistencias} assist. · ${p.totalBeneficios} benef.)`,
                }))}
                placeholder="Escolha o plano de origem"
                value={origem}
                disabled={copiando}
                onChange={(evento) => setOrigem(evento.target.value)}
              />
            </Field>

            <Botao
              variante="secundario"
              disabled={!origem}
              carregando={copiando}
              onClick={pedirCopia}
            >
              Copiar
            </Botao>
          </div>
        </FormSection>
      )}

      {SECOES.map((secao) => (
        <FormSection key={secao} titulo={ROTULOS[secao]} icone="documento">
          {secoes[secao].length === 0 ? (
            <p className="tw-m-0 tw-text-sm tw-text-texto-suave">
              Sem itens — a seção &quot;{ROTULOS[secao]}&quot; não aparecerá no PDF.
            </p>
          ) : (
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-4 tw-p-0">
              {secoes[secao].map((item, indice) => (
                <li
                  key={item.chave}
                  className="tw-grid tw-gap-3 md:tw-grid-cols-[1fr_1fr_auto] md:tw-items-start"
                >
                  <Field
                    rotulo={`Descrição — item ${indice + 1}`}
                    erro={erroItens[item.chave]}
                    campo={`item-${item.chave}`}
                  >
                    <Input
                      value={item.descricao}
                      maxLength={200}
                      placeholder="Coluna esquerda"
                      onChange={(evento) =>
                        alterarItem(secao, item.chave, 'descricao', evento.target.value)
                      }
                    />
                  </Field>

                  <Field rotulo={`Detalhe — item ${indice + 1}`} dica="Opcional">
                    <Input
                      value={item.detalhe ?? ''}
                      maxLength={2000}
                      placeholder="Coluna direita"
                      onChange={(evento) =>
                        alterarItem(secao, item.chave, 'detalhe', evento.target.value)
                      }
                    />
                  </Field>

                  {/* ⚠️ Os botões eram os glifos `↑` `↓` `×`, sem texto nenhum — o
                      rótulo vivia em `title` (regra de Victor: marcador é texto). */}
                  <div className="tw-flex tw-flex-wrap tw-gap-2 md:tw-pt-7">
                    <Botao
                      variante="fantasma"
                      tamanho="sm"
                      disabled={indice === 0}
                      aria-label={`Subir o item ${indice + 1} de ${ROTULOS[secao]}`}
                      onClick={() => moverItem(secao, indice, -1)}
                    >
                      Subir
                    </Botao>
                    <Botao
                      variante="fantasma"
                      tamanho="sm"
                      disabled={indice === secoes[secao].length - 1}
                      aria-label={`Descer o item ${indice + 1} de ${ROTULOS[secao]}`}
                      onClick={() => moverItem(secao, indice, 1)}
                    >
                      Descer
                    </Botao>
                    <Botao
                      variante="fantasma"
                      tamanho="sm"
                      aria-label={`Remover o item ${indice + 1} de ${ROTULOS[secao]}`}
                      onClick={() => removerItem(secao, item.chave)}
                    >
                      Remover
                    </Botao>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div>
            <Botao
              variante="secundario"
              tamanho="sm"
              iconeEsquerda="mais"
              onClick={() => adicionarItem(secao)}
            >
              Adicionar item
            </Botao>
          </div>
        </FormSection>
      ))}

      <FormSection titulo="Situação" icone="alerta">
        <Checkbox
          rotulo="Plano ativo"
          destaque
          descricao="Só planos ativos aparecem na tela de cotação. Deixar inativo registra que a associação não vende este tipo — e tira o aviso de pendência. Um plano ativo precisa ter ao menos um item."
          checked={ativo}
          onChange={(marcado) => {
            setAtivo(marcado);
            setErroAtivo('');
          }}
        />

        {/* A regra é do backend (400); a tela passa a dizer ANTES de chamar. */}
        {erroAtivo && (
          <p role="alert" className="tw-m-0 tw-text-xs tw-font-semibold tw-text-danger">
            {erroAtivo}
          </p>
        )}
      </FormSection>

      <FormActions>
        <Botao variante="secundario" onClick={voltar} disabled={salvando}>
          Cancelar
        </Botao>
        <Botao carregando={salvando} onClick={() => void salvar()}>
          Salvar
        </Botao>
      </FormActions>
    </div>
  );

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={6} altura="tw-h-12" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o plano."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            <Chip variante={ativo ? 'ok' : 'neutro'}>{ativo ? 'Ativo' : 'Inativo'}</Chip>
            {alertaTipo && <Chip variante={alertaTipo.variante}>{alertaTipo.texto}</Chip>}
            {!existente && <Chip variante="pendente">Ainda não salvo</Chip>}
          </div>

          {alertaTipo && <Nota variante="atencao">{alertaTipo.descricao}</Nota>}

          <ResumoRegistro
            linhas={[
              { rotulo: 'Código do tipo de veículo', valor: codigo },
              { rotulo: 'Tipo de veículo (SGA)', valor: descricaoTipo ?? '—' },
              {
                rotulo: 'Última edição',
                valor: atualizadoEm ? formatarDataCurta(atualizadoEm) : '—',
              },
            ]}
          />
        </div>

        <Tabs
          idBase={ID_BASE}
          abas={ABAS}
          ativa={aba}
          aoTrocar={(valor) => {
            if (valor === 'historico' && !existente) return;
            setAba(valor as Aba);
          }}
          rotuloAcessivel="Seções do plano"
        />

        {/* ⚠️ O motivo da aba travada em TEXTO — era um `title` no botão. */}
        {!existente && (
          <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
            O histórico fica disponível depois que o plano for salvo pela primeira vez.
          </p>
        )}

        <div
          id={idDoPainel(ID_BASE)}
          role="tabpanel"
          aria-labelledby={idDaAba(ID_BASE, aba)}
          tabIndex={-1}
        >
          {aba === 'conteudo' ? (
            painelConteudo()
          ) : (
            <HistoricoEdicoes
              itens={historico.map((versao) => ({
                id: versao.id,
                quando: versao.criadoEm,
                resumo: (
                  <>
                    <strong>{versao.snapshotAnterior?.nome ?? '—'}</strong> ·{' '}
                    {versao.snapshotAnterior?.coberturas?.length ?? 0} cob. ·{' '}
                    {versao.snapshotAnterior?.assistencias?.length ?? 0} assist. ·{' '}
                    {versao.snapshotAnterior?.beneficios?.length ?? 0} benef.
                  </>
                ),
                detalhe: (
                  <span className="tw-text-xs tw-text-texto-suave">
                    {versao.snapshotAnterior?.ativo === false ? 'Inativo' : 'Ativo'}
                  </span>
                ),
              }))}
              carregando={carregandoHistorico}
              erro={erroHistorico || undefined}
              aoTentarNovamente={() => void carregarHistorico()}
              vazio="Nenhuma edição registrada ainda."
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo={descricaoTipo ?? `Tipo ${codigo}`}
          sobretitulo="Planos de Cotação"
          aoVoltar={voltar}
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-4xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {conteudo()}
          </div>
        </div>
      </div>

      <ModalConfirmacao
        aberto={confirmarCopiaDe !== null}
        titulo="Substituir o conteúdo?"
        mensagem="Copiar o conteúdo de outro plano vai substituir as três seções deste."
        detalhe={`${totalItens} item(ns) já preenchido(s) aqui serão perdidos. O nome e a situação do plano não mudam.`}
        rotuloConfirmar="Copiar mesmo assim"
        destrutivo
        processando={copiando}
        onConfirmar={() => confirmarCopiaDe && void copiarConteudoDe(confirmarCopiaDe)}
        onCancelar={() => setConfirmarCopiaDe(null)}
      />
    </ShellSaac>
  );
};

export default PlanoFichaPage;
